import {inject, injectAsync, Injector, runInInjectionContext} from "@angular/core";
import type {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  GuardResult,
  MaybeAsync,
  RouterStateSnapshot,
} from "@angular/router";
import {PRIMARY_OUTLET, RedirectCommand, Router} from "@angular/router";
import {firstValueFrom, isObservable} from "rxjs";
import type {NonEmptyTuple} from "type-fest";
import type {StoredCredentials} from "../../../common/credential-storage";
import type {AppRoute} from "../../app.routes";
import {completionOf} from "../../helpers/RxJsOperators";
import {isNonOptionalNullable, type Optional, type OptionalNullable} from "../../helpers/TypeHelpers";
import type {PermissionId, Permissions} from "../../models/users/Permission";
import type {UserAccountType} from "../../models/users/User";
import {CredentialsStorage} from "../credentials-storage";

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
async function _readCurrentCredentials(credentialsStorage?: CredentialsStorage): Promise<Optional<StoredCredentials>> {
  credentialsStorage ??= inject(CredentialsStorage);
  const authApi = injectAsync(() => import("../api/auth-api").then(m => m.AuthApi));
  let credentials = credentialsStorage.nonExpiredCredentials();
  if (!credentials) {
    await completionOf(credentialsStorage.readCredentialsFromWebApiIfNeeded());
    credentials = credentialsStorage.nonExpiredCredentials();
  }
  if (credentials?.isAboutToExpire === true) {
    // We log out in this case, since it's a page load.
    // It's better we log out on a page load, than for example a saving request.
    await completionOf((await authApi()).logout());
    credentials = credentialsStorage.nonExpiredCredentials();
  }
  return credentials;
}

async function _readCurrentPermissions(): Promise<Permissions> {
  const credentialsStorage = inject(CredentialsStorage);
  const usersApi = injectAsync(() => import("../api/users/users-api").then(m => m.UsersApi));
  const credentials = await _readCurrentCredentials(credentialsStorage);
  if (credentials) await completionOf(credentialsStorage.refreshPermissionsIfNeeded(usersApi));
  return credentialsStorage.permissions();
}

export const isLoggedIn: CanActivateFn & CanActivateChildFn = async (
  _route: ActivatedRouteSnapshot, state: RouterStateSnapshot,
): Promise<GuardResult> => {
  const router = inject(Router); // injected here because are not allowed to do that after the first `await`.
  const credentials = await _readCurrentCredentials();
  if (credentials) {
    if (credentials.isValid) return true;
    if (credentials.needsPasswordChange) {
      const changePasswordUrl = router.parseUrl("/change-password" satisfies AppRoute);
      const changePasswordPrimaryOutlet = changePasswordUrl.root.children[PRIMARY_OUTLET];
      if (changePasswordPrimaryOutlet
        && changePasswordPrimaryOutlet.toString() === router.parseUrl(state.url).root.children[PRIMARY_OUTLET]?.toString())
        return true;
      changePasswordUrl.queryParams["return-url"] = state.url;
      return new RedirectCommand(changePasswordUrl);
    }
  }
  const loginUrl = router.parseUrl("/login" satisfies AppRoute);
  loginUrl.queryParams["return-url"] = state.url;
  return new RedirectCommand(loginUrl);
};

async function _checkUserTypes(_route: ActivatedRouteSnapshot,
                               _state: RouterStateSnapshot,
                               userTypes: readonly UserAccountType[]): Promise<GuardResult> {
  const credentials = await _readCurrentCredentials();
  return !!credentials && userTypes.includes(credentials.accountType);
}

export function hasUserTypes(...userTypes: NonEmptyTuple<UserAccountType>): CanActivateFn & CanActivateChildFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): MaybeAsync<GuardResult> =>
    _checkUserTypes(route, state, userTypes);
}

async function _checkPermissions(_route: ActivatedRouteSnapshot,
                                 _state: RouterStateSnapshot,
                                 requiredPermissions: NonEmptyTuple<PermissionId>): Promise<GuardResult> {
  const permissions = await _readCurrentPermissions();
  return permissions.hasPermissions(...requiredPermissions);
}

export function hasPermissions(...permissions: NonEmptyTuple<PermissionId>): CanActivateFn & CanActivateChildFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): MaybeAsync<GuardResult> =>
    _checkPermissions(route, state, permissions);
}

async function _checkUserId(_route: ActivatedRouteSnapshot,
                            _state: RouterStateSnapshot,
                            paramName: string,
                            type?: UserAccountType): Promise<GuardResult> {
  const credentials = await _readCurrentCredentials();
  if (!credentials) return false;
  const userIdParam: OptionalNullable<unknown> = _route.params[paramName];
  return isNonOptionalNullable(userIdParam) && Number(userIdParam) === credentials.userId
    && (type === undefined || credentials.accountType === type);
}

export function isMyUserId(paramName: string, userType?: UserAccountType): CanActivateFn & CanActivateChildFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): MaybeAsync<GuardResult> =>
    _checkUserId(route, state, paramName, userType);
}

export function and(...guards: NonEmptyTuple<CanActivateFn | CanActivateChildFn>): CanActivateFn & CanActivateChildFn {
  return async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<GuardResult> => {
    const injector = inject(Injector);
    for (const guard of guards) {
      const result = runInInjectionContext(injector, () => guard(route, state));
      let resolvedResult: GuardResult;
      if (result instanceof Promise) resolvedResult = await result;
      else if (isObservable(result)) resolvedResult = await firstValueFrom(result);
      else resolvedResult = result;
      if (resolvedResult !== true) return resolvedResult;
    }
    return true;
  };
}

export function or(...guards: NonEmptyTuple<CanActivateFn | CanActivateChildFn>): CanActivateFn & CanActivateChildFn {
  return async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<GuardResult> => {
    const injector = inject(Injector);
    let firstNonTrueResult: Optional<GuardResult> = undefined;
    for (const guard of guards) {
      const result = runInInjectionContext(injector, () => guard(route, state));
      let resolvedResult: GuardResult;
      if (result instanceof Promise) resolvedResult = await result;
      else if (isObservable(result)) resolvedResult = await firstValueFrom(result);
      else resolvedResult = result;
      if (resolvedResult === true) return true;
      firstNonTrueResult ??= resolvedResult;
    }
    return firstNonTrueResult ?? false;
  };
}
